import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { resolveLeadConnection } from './resolveLeadConnection';

const WhatsAppChat = ({ lead, pipelineConnections = [], onClose, onMensagemEnviada }) => {
    const [mensagens, setMensagens] = useState([]);
    const [novaMensagem, setNovaMensagem] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [erro, setErro] = useState(null);
    const [leadDetalhado, setLeadDetalhado] = useState(lead);
    const [instagramReplyVisibility, setInstagramReplyVisibility] = useState('private');
    const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
    const [previewArquivo, setPreviewArquivo] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);

    // ← Determina qual connection usar:
    // 1. connection_id do lead (de onde veio)
    // 2. Primeira conexão WhatsApp ativa da pipeline
    // 3. Fallback: 3
    const connection = useMemo(() => {
        return resolveLeadConnection(leadDetalhado, lead, pipelineConnections);
    }, [lead, leadDetalhado, pipelineConnections]);

    const connectionId = connection?.id || lead?.connection_id;
    const isInstagram = connection?.channel === 'instagram';
    const channelName = isInstagram ? 'Instagram' : 'WhatsApp';
    const channelColor = isInstagram ? '#c13584' : '#075e54';
    const channelAccent = isInstagram ? '#e1306c' : '#25d366';
    const channelIcon = isInstagram ? faInstagram : faWhatsapp;

    const ultimaInteracaoInstagram = useMemo(() => {
        if (!isInstagram) return null;
        return [...mensagens].reverse().find(item => {
            const dados = item.dados_novos || {};
            return item.tipo_interacao === 'instagram'
                && (!dados.connection_id || Number(dados.connection_id) === Number(connectionId));
        }) || null;
    }, [connectionId, isInstagram, mensagens]);

    const instagramContext = ultimaInteracaoInstagram?.dados_novos?.event_type;
    const isCommentContext = ['instagram_comment', 'instagram_live_comment'].includes(instagramContext);
    const currentCommentId = ultimaInteracaoInstagram?.dados_novos?.metadata?.comment_id;
    const privateReplyAlreadySent = useMemo(() => {
        if (!currentCommentId || !ultimaInteracaoInstagram) return false;

        const interactionTime = new Date(ultimaInteracaoInstagram.created_at).getTime();

        return mensagens.some(item => {
            const dados = item.dados_novos || {};
            if (item.tipo_interacao !== 'instagram_enviado' || dados.recipient_type !== 'comment') return false;

            return String(dados.source_comment_id || '') === String(currentCommentId)
                || (!dados.source_comment_id && new Date(item.created_at).getTime() >= interactionTime);
        });
    }, [currentCommentId, ultimaInteracaoInstagram, mensagens]);
    const leadId = lead?.id;

    useEffect(() => {
        setInstagramReplyVisibility(privateReplyAlreadySent ? 'public' : 'private');
    }, [instagramContext, privateReplyAlreadySent]);

    // Carrega o histórico e inicia polling
    useEffect(() => {
        if (!leadId) return;

        let isMounted = true;

        const carregarMensagens = (exibirLoading = false) => {
            if (exibirLoading) setIsLoading(true);
            setErro(null);

            return axios.get(`/api/leads/${leadId}`)
                .then(response => {
                    if (!isMounted) return;
                    const historicos = response.data.historicos || [];
                    setLeadDetalhado(response.data);
                    const ordenados = [...historicos].sort((a, b) =>
                        new Date(a.created_at) - new Date(b.created_at)
                    );

                    setMensagens(prev => {
                        const mensagensReais = prev.filter(m => !String(m.id).startsWith('temp-'));
                        if (ordenados.length > mensagensReais.length) {
                            return ordenados;
                        }
                        return prev;
                    });
                })
                .catch(() => {
                    if (isMounted) setErro('Não foi possível carregar o histórico.');
                })
                .finally(() => {
                    if (isMounted) setIsLoading(false);
                });
        };

        carregarMensagens(true);

        const interval = setInterval(() => {
            carregarMensagens(false);
        }, 5000);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [leadId]);

    // Scroll automático
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mensagens]);

    // Foca no input ao abrir
    useEffect(() => {
        if (!isLoading) inputRef.current?.focus();
    }, [isLoading]);

    const handleArquivoSelecionado = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const tipo = file.type.startsWith('image/') ? 'image'
            : file.type.startsWith('video/') ? 'video'
            : file.type.startsWith('audio/') ? 'audio'
            : 'document';

        setArquivoSelecionado({ file, tipo });

        if (tipo === 'image') {
            const reader = new FileReader();
            reader.onload = (e) => setPreviewArquivo(e.target.result);
            reader.readAsDataURL(file);
        } else {
            setPreviewArquivo(null);
        }

        e.target.value = '';
    };

    const handleEnviar = async () => {
        const texto = novaMensagem.trim();
        if (!texto || isSending) return;

        setNovaMensagem('');
        setIsSending(true);
        setErro(null);

        const mensagemTemp = {
            id: `temp-${Date.now()}`,
            nota: `📤 Mensagem enviada via WhatsApp: "${texto}"`,
            tipo_interacao: isInstagram ? 'instagram_enviado' : 'whatsapp_enviado',
            midia_url: null,
            midia_tipo: null,
            created_at: new Date().toISOString(),
            user: { nome: 'Você' },
            dados_novos: isInstagram && isCommentContext ? {
                recipient_type: instagramReplyVisibility === 'public' ? 'public_comment' : 'comment',
                reply_visibility: instagramReplyVisibility,
                source_comment_id: currentCommentId,
            } : {},
        };
        mensagemTemp.nota = isInstagram && isCommentContext && instagramReplyVisibility === 'public'
            ? 'Resposta pública enviada em comentário do Instagram: ' + texto
            : '📤 Mensagem enviada via ' + channelName + ': ' + texto;
        setMensagens(prev => [...prev, mensagemTemp]);

        try {
            if (!connectionId) throw new Error('Nenhuma conexão disponível para este lead.');

            const payload = isInstagram
                ? {
                    lead_id: lead.id,
                    interaction_id: ultimaInteracaoInstagram?.id,
                    message: texto,
                    reply_visibility: isCommentContext ? instagramReplyVisibility : 'private',
                }
                : { phone: (lead.telefone || '').replace(/\D/g, ''), lead_id: lead.id, message: texto };

            const response = await axios.post(`/api/connections/${connectionId}/send-message`, payload);

            // ← Verifica se a API retornou success: false (status 200 mas com erro)
            if (response.data?.success === false) {
                const mensagemErro = response.data?.message || 'Não foi possível enviar a mensagem.';
                setErro(`⚠️ ${mensagemErro}`);
                setMensagens(prev => prev.filter(m => m.id !== mensagemTemp.id));
                setNovaMensagem(texto);
                return;
            }

            setMensagens(prev =>
                prev.map(m =>
                    m.id === mensagemTemp.id
                        ? { ...mensagemTemp, id: Date.now() }
                        : m
                )
            );

            if (onMensagemEnviada) onMensagemEnviada(lead);

        } catch (err) {
            console.error('Erro ao enviar mensagem:', err);
            const mensagemErro = err.response?.data?.message || 'Não foi possível enviar a mensagem. Tente novamente.';
            setErro(`⚠️ ${mensagemErro}`);
            setMensagens(prev => prev.filter(m => m.id !== mensagemTemp.id));
            setNovaMensagem(texto);
        } finally {
            setIsSending(false);
            inputRef.current?.focus();
        }
    };


    const handleEnviarMidia = async () => {
        if (!arquivoSelecionado || isSending) return;

        if (isInstagram) {
            setErro('O envio de mídia pelo Instagram ainda não está disponível. Envie uma mensagem de texto.');
            return;
        }

        setIsSending(true);
        setErro(null);

        const icone = arquivoSelecionado.tipo === 'image' ? '🖼️ Imagem'
            : arquivoSelecionado.tipo === 'video' ? '🎥 Vídeo'
            : arquivoSelecionado.tipo === 'audio' ? '🎵 Áudio'
            : '📄 Documento';

        const mensagemTemp = {
            id: `temp-${Date.now()}`,
            nota: `📤 ${icone} enviado via WhatsApp.`,
            tipo_interacao: 'whatsapp_enviado',
            midia_url: previewArquivo,
            midia_tipo: arquivoSelecionado.tipo,
            created_at: new Date().toISOString(),
            user: { nome: 'Você' },
        };

        setMensagens(prev => [...prev, mensagemTemp]);
        const arquivoAtual = arquivoSelecionado;
        const captionAtual = novaMensagem.trim();
        setArquivoSelecionado(null);
        setPreviewArquivo(null);
        setNovaMensagem('');

        try {
            const telefoneNumeros = lead.telefone.replace(/\D/g, '');
            const formData = new FormData();
            formData.append('phone', telefoneNumeros);
            formData.append('file', arquivoAtual.file);
            formData.append('type', arquivoAtual.tipo);
            if (captionAtual) formData.append('caption', captionAtual);

            const response = await axios.post(`/api/connections/${connectionId}/send-media`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // ← Verifica se a API retornou success: false (status 200 mas com erro)
            if (response.data?.success === false) {
                const mensagemErro = response.data?.message || 'Não foi possível enviar a mídia.';
                setErro(`⚠️ ${mensagemErro}`);
                setMensagens(prev => prev.filter(m => m.id !== mensagemTemp.id));
                return;
            }

            setMensagens(prev =>
                prev.map(m =>
                    m.id === mensagemTemp.id
                        ? { ...mensagemTemp, id: Date.now() }
                        : m
                )
            );

            if (onMensagemEnviada) onMensagemEnviada(lead);

        } catch (err) {
            console.error('Erro ao enviar mídia:', err);
            const mensagemErro = err.response?.data?.message || 'Não foi possível enviar a mídia. Tente novamente.';
            setErro(`⚠️ ${mensagemErro}`);
            setMensagens(prev => prev.filter(m => m.id !== mensagemTemp.id));
        } finally {
            setIsSending(false);
            inputRef.current?.focus();
        }
    };


    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (arquivoSelecionado) {
                handleEnviarMidia();
            } else {
                handleEnviar();
            }
        }
    };

    const renderMensagem = (item) => {
        const isEnviada = item.nota?.startsWith('📤')
            || item.tipo_interacao === 'whatsapp_enviado'
            || item.tipo_interacao === 'instagram_enviado';
        const externalActor = item.dados_novos?.actor;
        const actorChannel = externalActor?.channel || (item.tipo_interacao === 'instagram' ? 'instagram' : null);
        const actorExternalUserId = externalActor?.external_user_id || item.dados_novos?.external_user_id;
        const externalIdentity = (leadDetalhado?.channel_identities || []).find(identity =>
            identity.channel === actorChannel
            && String(identity.external_user_id) === String(actorExternalUserId)
        );
        const senderName = externalActor?.username
            || externalActor?.display_name
            || externalIdentity?.username
            || externalIdentity?.display_name
            || lead?.nome
            || 'Lead';

        return (
            <div
                key={item.id}
                style={{
                    display: 'flex',
                    justifyContent: isEnviada ? 'flex-end' : 'flex-start',
                    marginBottom: '8px',
                    padding: '0 8px',
                }}
            >
                <div style={{
                    maxWidth: '75%',
                    backgroundColor: isEnviada ? '#dcf8c6' : '#ffffff',
                    borderRadius: isEnviada ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    padding: '8px 12px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                }}>
                    {/* ✅ CORRIGIDO — mostra o nome do lead, não do usuário */}
                    {!isEnviada && (
                        <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#075e54', fontWeight: 'bold', marginBottom: '2px' }}>
                            {senderName}
                        </div>
                    )}

                    <div style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#303030', wordBreak: 'break-word' }}>
                        {item.nota}
                    </div>

                    {item.dados_novos?.metadata?.media?.permalink && (
                        <a
                            href={item.dados_novos.metadata.media.permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'block', marginTop: '5px', color: '#c13584', fontSize: `calc(11px * var(--font-scale))` }}
                        >
                            Ver publicacao comentada
                        </a>
                    )}

                    {/* Imagem */}
                    {item.midia_url && item.midia_tipo === 'image' && (
                        <a href={item.midia_url} target="_blank" rel="noopener noreferrer">
                            <img
                                src={item.midia_url}
                                alt="Imagem"
                                style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', marginTop: '4px', display: 'block' }}
                            />
                        </a>
                    )}

                    {/* Vídeo */}
                    {item.midia_url && item.midia_tipo === 'video' && (
                        <video controls style={{ maxWidth: '200px', borderRadius: '8px', marginTop: '4px', display: 'block' }}>
                            <source src={item.midia_url} type="video/mp4" />
                        </video>
                    )}

                    {/* Áudio */}
                    {item.midia_url && item.midia_tipo === 'audio' && (
                        <audio controls style={{ width: '200px', marginTop: '4px' }}>
                            <source src={item.midia_url} type="audio/ogg" />
                            <source src={item.midia_url} type="audio/mpeg" />
                        </audio>
                    )}

                    {/* Documento */}
                    {item.midia_url && item.midia_tipo === 'document' && (
                        <a
                            href={item.midia_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                marginTop: '4px',
                                color: '#075e54',
                                fontSize: `calc(12px * var(--font-scale))`,
                                textDecoration: 'none',
                            }}
                        >
                            <FontAwesomeIcon icon="file" />
                            Abrir documento
                        </a>
                    )}

                    {/* Sticker */}
                    {item.midia_url && item.midia_tipo === 'sticker' && (
                        <img src={item.midia_url} alt="Sticker" style={{ maxWidth: '100px', marginTop: '4px', display: 'block' }} />
                    )}

                    {/* Horário + status */}
                    <div style={{ fontSize: `calc(10px * var(--font-scale))`, color: '#999', textAlign: 'right', marginTop: '4px' }}>
                        {format(parseISO(item.created_at), "HH:mm")}
                        {isEnviada && (
                            <FontAwesomeIcon
                                icon="check-double"
                                style={{ marginLeft: '4px', color: String(item.id).startsWith('temp') ? '#999' : '#53bdeb' }}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };


    return (
        <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '360px',
            height: '560px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9999,
            overflow: 'hidden',
            fontFamily: "'Segoe UI', sans-serif",
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: channelColor,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
            }}>
                <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#128c7e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    flexShrink: 0,
                }}>
                    {lead?.nome?.charAt(0)?.toUpperCase() || '?'}
                </div>

                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ color: '#fff', fontWeight: 'bold', fontSize: `calc(15px * var(--font-scale))`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {lead?.nome || 'Lead'}
                    </div>
                    <div style={{ color: '#b2dfdb', fontSize: `calc(12px * var(--font-scale))` }}>
                        {isInstagram
                            ? (leadDetalhado?.channel_identities?.find(identity => Number(identity.connection_id) === Number(connectionId))?.username || 'Instagram')
                            : (lead?.telefone || '')}
                    </div>
                </div>

                {/* ← Indicador da conexão sendo usada */}
                <div title={`Usando conexão ID: ${connectionId}`}>
                    <FontAwesomeIcon icon={channelIcon} style={{ color: channelAccent, fontSize: '20px' }} />
                </div>

                <button
                    onClick={onClose}
                    style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '18px', padding: '4px', lineHeight: 1 }}
                >
                    <FontAwesomeIcon icon="times" />
                </button>
            </div>

            {/* Área de mensagens */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '12px 0',
                backgroundColor: '#e5ddd5',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c5b9b0' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <FontAwesomeIcon icon="spinner" spin style={{ fontSize: '24px', marginBottom: '8px' }} />
                        <div>Carregando conversa...</div>
                    </div>
                ) : mensagens.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666', fontSize: `calc(13px * var(--font-scale))` }}>
                        <FontAwesomeIcon icon={channelIcon} style={{ fontSize: '40px', color: channelAccent, display: 'block', margin: '0 auto 8px' }} />
                        Nenhuma mensagem ainda.<br />
                        Inicie a conversa abaixo!
                    </div>
                ) : (
                    mensagens.map(item => renderMensagem(item))
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Erro */}
            {erro && (
                <div style={{
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    padding: '6px 12px',
                    fontSize: `calc(12px * var(--font-scale))`,
                    textAlign: 'center',
                    borderTop: '1px solid #ffcdd2',
                }}>
                    {erro}
                </div>
            )}

            {isInstagram && (
                <div style={{ padding: '6px 12px', fontSize: `calc(11px * var(--font-scale))`, color: '#6a1b4d', backgroundColor: '#fce4ec', textAlign: 'center' }}>
                    {isCommentContext
                        ? (
                            <>
                                <div style={{ marginBottom: '6px' }}>
                                    Escolha como responder ao {instagramContext === 'instagram_live_comment' ? 'comentário da live' : 'comentário'}.
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                                    {[
                                        {
                                            value: 'private',
                                            label: privateReplyAlreadySent ? 'Privado já enviado' : 'Responder em privado',
                                            disabled: privateReplyAlreadySent,
                                        },
                                        { value: 'public', label: 'Responder publicamente' },
                                    ].map(option => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => !option.disabled && setInstagramReplyVisibility(option.value)}
                                            disabled={option.disabled}
                                            style={{
                                                border: '1px solid #c13584',
                                                borderRadius: '16px',
                                                padding: '5px 10px',
                                                cursor: option.disabled ? 'not-allowed' : 'pointer',
                                                opacity: option.disabled ? 0.55 : 1,
                                                fontSize: `calc(11px * var(--font-scale))`,
                                                backgroundColor: instagramReplyVisibility === option.value ? '#c13584' : '#fff',
                                                color: instagramReplyVisibility === option.value ? '#fff' : '#c13584',
                                            }}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )
                        : instagramContext === 'instagram_story_reply'
                            ? 'Respondendo à conversa iniciada por uma Story.'
                            : 'Respondendo pelo Direct do Instagram.'}
                </div>
            )}

            {/* Input */}
            <div style={{
                backgroundColor: '#f0f0f0',
                padding: '8px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                borderTop: '1px solid #ddd',
            }}>
                {/* Preview do arquivo selecionado */}
                {arquivoSelecionado && (
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: `calc(13px * var(--font-scale))`,
                        border: '1px solid #ddd',
                    }}>
                        {previewArquivo ? (
                            <img src={previewArquivo} alt="preview" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                        ) : (
                            <FontAwesomeIcon
                                icon={
                                    arquivoSelecionado.tipo === 'video' ? 'film' :
                                    arquivoSelecionado.tipo === 'audio' ? 'music' : 'file'
                                }
                                style={{ fontSize: '20px', color: '#075e54' }}
                            />
                        )}
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {arquivoSelecionado.file.name}
                        </span>
                        <button
                            onClick={() => { setArquivoSelecionado(null); setPreviewArquivo(null); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999' }}
                        >
                            <FontAwesomeIcon icon="times" />
                        </button>
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    {/* Input de arquivo oculto */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                        style={{ display: 'none' }}
                        onChange={handleArquivoSelecionado}
                    />

                    {/* Botão clipe */}
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isSending || isInstagram}
                        title="Anexar arquivo"
                        style={{
                            width: '42px', height: '42px', borderRadius: '50%',
                            backgroundColor: '#fff', border: '1px solid #ddd',
                            color: isInstagram ? '#aaa' : channelColor, cursor: isInstagram ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '16px', flexShrink: 0,
                        }}
                    >
                        <FontAwesomeIcon icon="paperclip" />
                    </button>

                    <textarea
                        ref={inputRef}
                        value={novaMensagem}
                        onChange={(e) => setNovaMensagem(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={arquivoSelecionado ? "Adicionar legenda..." : "Digite uma mensagem..."}
                        rows={1}
                        style={{
                            flex: 1, border: 'none', borderRadius: '20px',
                            padding: '10px 16px', fontSize: `calc(14px * var(--font-scale))`,
                            resize: 'none', outline: 'none',
                            backgroundColor: '#fff', maxHeight: '100px',
                            overflowY: 'auto', lineHeight: '1.4',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        }}
                    />

                    {/* Botão enviar */}
                    <button
                        onClick={arquivoSelecionado ? handleEnviarMidia : handleEnviar}
                        disabled={(!novaMensagem.trim() && !arquivoSelecionado) || isSending}
                        style={{
                            width: '42px', height: '42px', borderRadius: '50%',
                            backgroundColor: (novaMensagem.trim() || arquivoSelecionado) && !isSending ? channelColor : '#b0bec5',
                            border: 'none', color: '#fff',
                            cursor: (novaMensagem.trim() || arquivoSelecionado) && !isSending ? 'pointer' : 'default',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '16px', transition: 'background-color 0.2s', flexShrink: 0,
                        }}
                    >
                        <FontAwesomeIcon icon={isSending ? 'spinner' : 'paper-plane'} spin={isSending} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WhatsAppChat;
